/**
 * Compilación del layout a un modelo espacial (componente 7 de la auditoría del v6, adaptado):
 * convierte elementos y vías en estaciones con bocas de acceso, un grafo de circulación, la matriz
 * de distancias por ruta y las rutas que dibuja la escena. Mover un elevador o una vía recalcula
 * distancias y tiempos de traslado. Si no existe ruta, la validación bloquea (nunca línea recta).
 */

import type { Catalogo, Elemento, Espacio, Punto, TipoBahia } from '../dominio/espacio';
import type { Observacion } from '../dominio/observacion';
import type { TipoDeUnidad, TipoEstacion } from '../dominio/proceso';
import {
  ALCANCE_BOCA_M,
  bocasDeElemento,
  giroHaciaVia,
  girosDeVecinos,
  MARCADOR_MAX_M,
  type BocaEnPlanta,
} from './bocas';
import {
  caminosMinimos,
  construirGrafo,
  reconstruirCamino,
  tramosVehiculares,
  type GrafoCirculacion,
} from './circulacion';
import { distancia } from './geometria';
import { extremosSueltos, type ExtremoSuelto } from './vias';

export interface EstacionEnPlanta {
  /** Código visible: B01, R01, P01, L01, E01, ING, SAL. */
  id: string;
  tipo: TipoEstacion;
  elementoId: string;
  centro: Punto;
  rotacion: number;
  bocaEntrada: Punto | null;
  bocaSalida: Punto | null;
  /** Bocas en el borde del elemento (paso 1.4): el plano las dibuja verdes o rojas. */
  bocas: BocaEnPlanta[];
  capacidad: number | null;
  /** Tipos de unidad que admite; `null` = todos. */
  compatibles: string[] | null;
  especializada: boolean;
  sigueTurno: boolean;
}

/**
 * Servicios que admite un tipo de bahía, según lo que declara cada servicio. Un tipo no
 * especializado que admiten todos queda abierto (`null`), como el elevador mixto.
 */
export function compatiblesDeBahia(
  tipo: TipoBahia,
  tipos: readonly TipoDeUnidad[],
  especializada: boolean,
): string[] | null {
  if (tipo === 'inactivo') return [];
  const admiten = tipos.filter((t) => t.bahias.includes(tipo)).map((t) => t.id);
  return !especializada && admiten.length === tipos.length ? null : admiten;
}

export interface ModeloEspacial {
  estaciones: EstacionEnPlanta[];
  grafo: GrafoCirculacion;
  /** Metros por la ruta más corta (índices de `estaciones`); `null` = sin ruta. */
  distancias: (number | null)[][];
  /** Polilínea de cada ruta, con clave «origen>destino». */
  rutas: Record<string, Punto[]>;
  /** Elementos que quedaron fuera del marco del espacio (no se simulan). */
  fueraDelEspacio: string[];
  /** Extremos de vías vehiculares que no se unen a nada (paso 1.4). */
  extremosSueltos: ExtremoSuelto[];
  observaciones: Observacion[];
}

const PREFIJO: Record<TipoEstacion, string> = {
  bahia: 'B',
  recepcion: 'R',
  patio: 'P',
  lavado: 'L',
  entrega: 'E',
  ingreso: 'ING',
  salida: 'SAL',
};

export const NOMBRE_ESTACION: Record<TipoEstacion, string> = {
  bahia: 'Elevador',
  recepcion: 'Cajón de recepción',
  patio: 'Cajón de patio',
  lavado: 'Puesto de lavado',
  entrega: 'Cajón de entrega',
  ingreso: 'Ingreso vehicular',
  salida: 'Salida vehicular',
};

export function dentroDelMarco(e: Elemento, marco: Espacio['marco']): boolean {
  return (
    Math.abs(e.posicion.x - marco.x) <= marco.ancho / 2 + 1e-6 &&
    Math.abs(e.posicion.z - marco.z) <= marco.fondo / 2 + 1e-6
  );
}

export function compilarEspacio(espacio: Espacio, catalogo: Catalogo): ModeloEspacial {
  const observaciones: Observacion[] = [];
  const tramos = tramosVehiculares(espacio.layout.vias);
  const estaciones: EstacionEnPlanta[] = [];
  const fueraDelEspacio: string[] = [];
  const contador: Partial<Record<TipoEstacion, number>> = {};

  for (const e of espacio.layout.elementos) {
    const item = catalogo[e.catalogoId];
    const rol = item?.rolOperativo;
    if (!item) continue;
    if (!dentroDelMarco(e, espacio.marco)) {
      fueraDelEspacio.push(e.id);
      continue;
    }
    // Solo las estaciones con boca vehicular reciben autos.
    const boca = item.boca;
    if (!rol || !boca || boca.circulacion !== 'vehicular') continue;
    const tipoBahia = e.propiedades?.tipoBahia ?? 'mixto';
    const variante = rol.variantes ? rol.variantes[tipoBahia] : undefined;
    if (variante && !variante.activa) continue;

    const n = (contador[rol.estacion] ?? 0) + 1;
    if ((rol.estacion === 'ingreso' || rol.estacion === 'salida') && n > 1) continue; // se advierte en validación
    contador[rol.estacion] = n;
    const id =
      boca.forma === 'marcador'
        ? PREFIJO[rol.estacion]
        : `${PREFIJO[rol.estacion]}${String(n).padStart(2, '0')}`;
    const bocas = bocasDeElemento(e, boca, tramos);
    let centro = e.posicion;
    let bocaEntrada: Punto | null = null;
    let bocaSalida: Punto | null = null;
    const etiqueta =
      `${NOMBRE_ESTACION[rol.estacion]} ${boca.forma === 'marcador' ? '' : id}`.trim();

    if (boca.forma === 'marcador') {
      const enVia = bocas[0]!.via;
      if (!enVia) {
        observaciones.push({
          regla: 'marcador-lejos-de-via',
          nivel: 'bloquea',
          que: `${NOMBRE_ESTACION[rol.estacion]} lejos de una vía vehicular`,
          donde: `${NOMBRE_ESTACION[rol.estacion]} (a más de ${MARCADOR_MAX_M} m de cualquier vía)`,
          porque: 'Los autos no pueden entrar ni salir del taller si el punto no toca una vía.',
          queHacer: `Acércalo a una vía (a menos de ${MARCADOR_MAX_M} m) o dibuja una vía que pase por él.`,
          elementos: [e.id],
        });
      } else {
        centro = enVia;
        bocaEntrada = enVia;
        bocaSalida = enVia;
      }
    } else {
      bocaEntrada = bocas[0]!.via;
      bocaSalida = bocas.find((b) => b.rol === 'salida')?.via ?? bocaEntrada;
      if (!bocaEntrada) {
        const lado = boca.forma === 'delante' ? 'delante' : 'detrás';
        const giro = giroHaciaVia(e, boca, tramos, girosDeVecinos(e, espacio.layout.elementos));
        observaciones.push({
          regla: 'estacion-sin-acceso',
          nivel: 'bloquea',
          que: `${etiqueta} sin acceso`,
          donde: etiqueta,
          porque: `Su boca de acceso (${lado}) no llega a ninguna vía vehicular en ${ALCANCE_BOCA_M} m, así que ningún auto puede entrar.`,
          queHacer:
            giro === null
              ? `No hay una vía a menos de ${ALCANCE_BOCA_M} m en ninguna dirección: acércalo a una vía o dibuja una vía ${lado} del elemento.`
              : 'Usa «Orientar hacia la vía»: lo gira para que su boca mire a la vía más cercana. También puedes acercarlo o dibujar una vía.',
          elementos: [e.id],
          ...(giro === null
            ? {}
            : {
                accion: { tipo: 'orientar-hacia-via' as const, elementoId: e.id, rotacion: giro },
              }),
        });
      }
    }
    estaciones.push({
      id,
      tipo: rol.estacion,
      elementoId: e.id,
      centro,
      rotacion: e.rotacion,
      bocaEntrada,
      bocaSalida,
      bocas,
      capacidad: rol.capacidad,
      compatibles: variante
        ? compatiblesDeBahia(
            tipoBahia,
            espacio.modelo.proceso.tiposDeUnidad,
            variante.especializada,
          )
        : null,
      especializada: variante ? variante.especializada : false,
      sigueTurno: rol.sigueTurno,
    });
  }

  const anclas: Punto[] = [];
  for (const s of estaciones) {
    if (s.bocaEntrada) anclas.push(s.bocaEntrada);
    if (s.bocaSalida && s.bocaSalida !== s.bocaEntrada) anclas.push(s.bocaSalida);
  }
  const grafo = construirGrafo(espacio.layout.vias, anclas);
  const nodoEntrada = estaciones.map((s) =>
    s.bocaEntrada ? grafo.buscar(s.bocaEntrada) : undefined,
  );
  const nodoSalida = estaciones.map((s) => (s.bocaSalida ? grafo.buscar(s.bocaSalida) : undefined));

  const n = estaciones.length;
  const distancias: (number | null)[][] = Array.from({ length: n }, () =>
    new Array<number | null>(n).fill(null),
  );
  const rutas: Record<string, Punto[]> = {};
  for (let i = 0; i < n; i++) {
    const a = estaciones[i]!;
    distancias[i]![i] = 0;
    const origen = nodoSalida[i];
    if (origen === undefined || !a.bocaSalida) continue;
    const { dist, previo } = caminosMinimos(grafo, origen);
    for (let j = 0; j < n; j++) {
      if (i === j) continue;
      const b = estaciones[j]!;
      const destino = nodoEntrada[j];
      if (destino === undefined || !b.bocaEntrada || !Number.isFinite(dist[destino]!)) continue;
      distancias[i]![j] =
        distancia(a.centro, a.bocaSalida) + dist[destino]! + distancia(b.bocaEntrada, b.centro);
      const puntos: Punto[] = [a.centro, a.bocaSalida];
      for (const k of reconstruirCamino(previo, destino))
        puntos.push({ x: grafo.nodos[k]!.x, z: grafo.nodos[k]!.z });
      puntos.push(b.bocaEntrada, b.centro);
      rutas[`${a.id}>${b.id}`] = depurar(puntos);
    }
  }
  return {
    estaciones,
    grafo,
    distancias,
    rutas,
    fueraDelEspacio,
    extremosSueltos: extremosSueltos(espacio, anclas),
    observaciones,
  };
}

/** Quita puntos repetidos consecutivos. */
function depurar(puntos: Punto[]): Punto[] {
  const r: Punto[] = [];
  for (const q of puntos) {
    const ultimo = r[r.length - 1];
    if (!ultimo || distancia(ultimo, q) > 0.01) r.push(q);
  }
  return r;
}
